import { CardMedia, Paper, styled, Skeleton } from "@mui/material";
import { useState, useEffect, useRef, memo } from "react";
import { useAuth } from "../../contexts/AuthContext";

// Preload the fallback image
const FALLBACK_IMAGE = "/no-hero.png"; // Path to your fallback image
const preloadFallbackImage = new Image();
preloadFallbackImage.src = FALLBACK_IMAGE;

// Memoize the styled component outside to prevent re-creation
const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 2,
  elevation: 3,
  overflow: "hidden",
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));

// Create a cache for API responses
const imageCache = new Map();

const CardMediaHero = ({ divesiteId }) => {
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const { currentUser, userClaims } = useAuth();
  const module = "CardMediaHero";

  // Determine image size based on claims (same logic as CardMediaImages)
  const getImageSizeFromClaims = () => {
    if (!userClaims) {
      console.debug(
        `[${module}] User claims not available, defaulting to small image size.`
      );
      return "small"; // Default to small
    }
    if (userClaims.isTier5User || userClaims.isTier6User) {
      console.debug(`[${module}] User is Tier 5 or 6, using large image size.`);
      return "large";
    }
    if (userClaims.isTier3User || userClaims.isTier4User) {
      console.debug(
        `[${module}] User is Tier 3 or 4, using medium image size.`
      );
      return "medium";
    }
    console.debug(
      `[${module}] User tier is 1 or 2 (or not matched), using small image size.`
    );
    return "small";
  };

  const size = getImageSizeFromClaims();
  console.debug(`[${module}] Determined image size:`, size);

  useEffect(() => {
    console.debug(
      `[${module}] useEffect triggered for divesiteId:`,
      divesiteId
    );
    if (!divesiteId) {
      console.debug(
        `[${module}] useEffect: No divesiteId provided, skipping fetch.`
      );
      return;
    }

    // Cancel any in-flight request when component unmounts or divesiteId changes
    if (abortControllerRef.current) {
      console.debug(`[${module}] useEffect: Aborting previous fetch.`);
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchHeroImage = async () => {
      // Check cache first
      const cacheKey = `hero-${divesiteId}-${size}`;
      if (imageCache.has(cacheKey)) {
        console.debug(
          `[${module}] fetchHeroImage: Image found in cache for key:`,
          cacheKey
        );
        setHeroImageUrl(imageCache.get(cacheKey));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.debug(
          `[${module}] fetchHeroImage: Fetching image from API for divesiteId:`,
          divesiteId,
          "and size:",
          size
        );
        // Use AbortController for cancelable fetch
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/media/divesite/${divesiteId}`,
          {
            signal: abortControllerRef.current.signal,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch media content: ${response.status}`);
        }

        const data = await response.json();
        console.debug(
          `[${module}] fetchHeroImage: API response received:`,
          data
        );
        // Find hero image from the returned data
        const heroMediaContent = data.find(
          (media) => media.tags && media.tags.includes("hero")
        );

        let url = null;
        if (heroMediaContent && heroMediaContent.mediaUrl) {
          url =
            heroMediaContent.mediaUrl[size] ||
            heroMediaContent.mediaUrl.original ||
            null;

          // Cache the result
          if (url) {
            imageCache.set(cacheKey, url);
            console.debug(
              `[${module}] fetchHeroImage: Image URL cached for key:`,
              cacheKey
            );
            // Preload the image
            const img = new Image();
            img.src = url;
          }
        }

        setHeroImageUrl(url);
        console.debug(
          `[${module}] fetchHeroImage: Hero URL:`,
          url,
          "for size:",
          size
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          setError(error);
          console.error(
            `[${module}] fetchHeroImage: Error fetching hero image:`,
            error
          );
        } else {
          console.debug(`[${module}] fetchHeroImage: Fetch aborted.`);
        }
      } finally {
        setLoading(false);
        console.debug(`[${module}] fetchHeroImage: Fetch completed.`);
      }
    };

    fetchHeroImage();

    return () => {
      // Cleanup function to abort fetch when component unmounts
      if (abortControllerRef.current) {
        console.debug(`[${module}] useEffect: Cleanup - Aborting fetch.`);
        abortControllerRef.current.abort();
      }
    };
  }, [divesiteId, userClaims, size]); // Re-run effect when userClaims or size changes

  // Optimize rendering with constants for better performance
  const skeletonHeight = 200;
  const imageHeight = 200;
  const skeletonProps = {
    variant: "rectangular",
    height: skeletonHeight,
    animation: "wave",
    sx: { bgcolor: "grey.200" },
  };

  const imageProps = {
    component: "img",
    height: imageHeight,
    loading: "eager", // Load this important image eagerly
    decoding: "async", // Allow the browser to decode the image asynchronously
  };

  // Return the appropriate element based on state
  return (
    <StyledPaper>
      {loading ? (
        <Skeleton {...skeletonProps} />
      ) : (
        <CardMedia
          {...imageProps}
          image={heroImageUrl || FALLBACK_IMAGE}
          alt={heroImageUrl ? "Divesite hero image" : "Scuba diver in a cenote"}
        />
      )}
    </StyledPaper>
  );
};

// Use memo to prevent unnecessary re-renders
export const MemoizedCardMediaHero = memo(CardMediaHero);
export { CardMediaHero };
